import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';

import { useViewer } from '@/entities/viewer';
import { CalendarView } from '@/features/calendar';
import { HubNav } from '@/features/hub-nav';
import { taskPath } from '@/entities/my-tasks';

import { MyTasksList } from './MyTasksList';
import styles from './MyTasksPage.module.css';
import { parseView, readStoredView, storeView, type MyTasksView } from './view-preference';

const VIEWS: readonly MyTasksView[] = ['calendar', 'list'];

/**
 * `/app/my-tasks`: the viewer's own tasks across every board - not a board, a planner. Calendar
 * (default) and list are two presentations of the same server-side selection; the choice is in
 * the URL (links, back / forward) and remembered for next time.
 */
export function MyTasksPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const viewer = useViewer();
  const user = viewer.status === 'authenticated' ? viewer.user : null;
  const view = parseView(params.get('view')) ?? readStoredView() ?? 'calendar';

  const choose = (next: MyTasksView) => {
    if (next === view) return;
    storeView(next);
    const nextParams = new URLSearchParams(params);
    nextParams.set('view', next);
    setParams(nextParams);
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.titles}>
          <h1 className={styles.title}>{t('myTasks.title')}</h1>
          <p className={styles.subtitle}>{t('myTasks.subtitle')}</p>
        </div>
        <div className={styles.tools}>
          <div className={styles.switcher} role="group" aria-label={t('myTasks.view')}>
            {VIEWS.map((option) => (
              <button
                key={option}
                type="button"
                className={`${styles.option} ${option === view ? styles.active : ''}`}
                aria-pressed={option === view}
                data-view={option}
                onClick={() => {
                  choose(option);
                }}
              >
                {t(`myTasks.views.${option}`)}
              </button>
            ))}
          </div>
        </div>
      </header>
      <HubNav />
      {view === 'calendar' ? (
        <CalendarView
          scope={{ kind: 'me' }}
          boards={[]}
          canEdit={false}
          firstDay={user?.first_day_of_week ?? 0}
          timeZone={user?.timezone ?? null}
          locale={i18n.language}
          onOpenCard={(cardId, boardId) => void navigate(taskPath(boardId, cardId))}
        />
      ) : (
        <MyTasksList />
      )}
    </div>
  );
}
